import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/InstagramAccounts_AuthUrlInput.ts';
import '../contracts/InstagramAccounts_ConnectInput.ts';
import '../contracts/InstagramAccounts_ExchangeTokenInput.ts';
import '../contracts/InstagramAccounts_SalvarFranquiasInput.ts';
import '../contracts/InstagramAccounts_SalvarTokenInput.ts';
import '../contracts/InstagramAccounts_ToggleActiveInput.ts';

import '../contracts/InstagramAccounts_AuthUrlResponse';
import '../contracts/InstagramAccounts_BuscaResponse';
import '../contracts/InstagramAccounts_ConnectResponse';
import '../contracts/InstagramAccounts_ExchangeTokenResponse';
import '../contracts/InstagramAccounts_ListaResponse';
import '../contracts/InstagramAccounts_RemoverResponse';
import '../contracts/InstagramAccounts_SalvarFranquiasResponse';
import '../contracts/InstagramAccounts_SalvarTokenResponse';
import '../contracts/InstagramAccounts_ToggleActiveResponse';
import type { InstagramAccounts_ConnectInput } from '../contracts/InstagramAccounts_ConnectInput';
import type { InstagramAccounts_ExchangeTokenInput } from '../contracts/InstagramAccounts_ExchangeTokenInput';
import type { InstagramAccounts_SalvarFranquiasInput } from '../contracts/InstagramAccounts_SalvarFranquiasInput';
import type { InstagramAccounts_SalvarTokenInput } from '../contracts/InstagramAccounts_SalvarTokenInput';
import type { InstagramAccounts_ToggleActiveInput } from '../contracts/InstagramAccounts_ToggleActiveInput';
export function useInstagramAccounts() {
  const isLoading = ref(false);
  const error = ref(null);

  const getAuthUrl = async () => {
    log('useInstagramAccounts', 'getAuthUrl', 'Fetching Instagram auth URL');
    isLoading.value = true;
    error.value = null;
    const redirectUri = 'https://localhost/instagram-oauth-callback';
    try {
      const response = await httpService.post('/index.php/instagram/accounts/auth_url', { redirect_uri: redirectUri }, { inputContract: 'InstagramAccounts_AuthUrlInput', responseContract: 'InstagramAccounts_AuthUrlResponse' });
      if (response.success) return response.dados;
      throw new Error(response.error || response.error || 'Erro ao buscar URL de autenticação');
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const exchangeToken = async (code: any, redirectUri: any) => {
    log('useInstagramAccounts', 'exchangeToken', 'Exchanging authorization code');
    isLoading.value = true;
    error.value = null;
    try {
      const payload: InstagramAccounts_ExchangeTokenInput = { code, redirect_uri: redirectUri };
      validate('InstagramAccounts_ExchangeTokenInput', payload);
      const response = await httpService.post('/index.php/instagram/accounts/exchange_token', payload, { responseContract: 'InstagramAccounts_ExchangeTokenResponse' });
      if (response.success) return response.dados;
      throw new Error(response.error || response.error || 'Erro ao trocar código por token');
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const connectAccount = async (pageData: InstagramAccounts_ConnectInput) => {
    log('useInstagramAccounts', 'connectAccount', 'Connecting Instagram account', pageData);
    isLoading.value = true;
    error.value = null;
    try {
      validate('InstagramAccounts_ConnectInput', pageData);
      const response = await httpService.post('/index.php/instagram/accounts/connect', pageData, { responseContract: 'InstagramAccounts_ConnectResponse' });
      if (response.success) return response.dados;
      throw new Error(response.error || response.error || 'Erro ao conectar conta Instagram');
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchLista = async () => {
    log('useInstagramAccounts', 'fetchLista', 'Fetching Instagram accounts list');
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get('/index.php/instagram/accounts/lista', { responseContract: 'InstagramAccounts_ListaResponse' });
      if (response.success) return response.dados || [];
      throw new Error(response.error || response.error || 'Erro ao buscar contas Instagram');
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchInstagramAccount = async (instagramAccountId: number) => {
    log('useInstagramAccounts', 'fetchInstagramAccount', 'Fetching account', instagramAccountId);
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`/index.php/instagram/accounts/busca/${instagramAccountId}`, { responseContract: 'InstagramAccounts_BuscaResponse' });
      if (response.success) return response.dados;
      throw new Error(response.error || response.error || 'Erro ao buscar conta Instagram');
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const salvarFranquias = async (instagramAccountId: any, franquiaIds: any) => {
    log('useInstagramAccounts', 'salvarFranquias', 'Saving franchise associations', { instagramAccountId, franquiaIds });
    isLoading.value = true;
    error.value = null;
    try {
      const payload: InstagramAccounts_SalvarFranquiasInput = {
        instagram_account_id: instagramAccountId,
        franquia_ids: franquiaIds,
      };
      validate('InstagramAccounts_SalvarFranquiasInput', payload);
      const response = await httpService.post('/index.php/instagram/accounts/salvar_franquias', payload, { responseContract: 'InstagramAccounts_SalvarFranquiasResponse' });
      if (response.success) return response.dados;
      throw new Error(response.error || response.error || 'Erro ao salvar franquias');
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const toggleActive = async (instagramAccountId: any, active: any) => {
    log('useInstagramAccounts', 'toggleActive', 'Toggling active', { instagramAccountId, active });
    try {
      const payload: InstagramAccounts_ToggleActiveInput = {
        instagram_account_id: instagramAccountId,
        active,
      };
      validate('InstagramAccounts_ToggleActiveInput', payload);
      const response = await httpService.post('/index.php/instagram/accounts/toggle_active', payload, { responseContract: 'InstagramAccounts_ToggleActiveResponse' });
      if (response.success) return response;
      throw new Error(response.error || response.error || 'Erro ao atualizar status');
    } catch (err) {
      error.value = err.message;
      throw err;
    }
  };

  const salvarToken = async (instagramAccountId: any, pageAccessToken: any) => {
    log('useInstagramAccounts', 'salvarToken', 'Updating token', instagramAccountId);
    isLoading.value = true;
    error.value = null;
    try {
      const payload: InstagramAccounts_SalvarTokenInput = {
        instagram_account_id: instagramAccountId,
        page_access_token: pageAccessToken,
      };
      validate('InstagramAccounts_SalvarTokenInput', payload);
      const response = await httpService.post('/index.php/instagram/accounts/salvar_token', payload, { responseContract: 'InstagramAccounts_SalvarTokenResponse' });
      if (response.success) return response;
      throw new Error(response.error || response.error || 'Erro ao atualizar token');
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const removerInstagramAccount = async (instagramAccountId: number) => {
    log('useInstagramAccounts', 'removerInstagramAccount', 'Removing account', instagramAccountId);
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`/index.php/instagram/accounts/remover/${instagramAccountId}`, { responseContract: 'InstagramAccounts_RemoverResponse' });
      if (response.success) return response;
      throw new Error(response.error || response.error || 'Erro ao remover conta Instagram');
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,
    getAuthUrl,
    exchangeToken,
    connectAccount,
    fetchLista,
    fetchInstagramAccount,
    salvarFranquias,
    salvarToken,
    toggleActive,
    removerInstagramAccount,
  };
}
